/**
 * Локальный пошаговый тур (localhost). Тексты перенесены из бывшего OnboardingModal DEFAULT_SECTIONS;
 * этапы 2 и 4 расширены по методичкам из локальной папки share/ (не в git): инструкция по рискам, туториал кризиса.
 *
 * Шаг: highlightId — data-tutor-highlight; null — только карточка по центру.
 * expandNav: развернуть верхнюю панель перед шагом.
 * skipIfNoElement: не показывать шаг, если якоря нет в DOM.
 * tourCardPlacement / tourCardAnchor — опционально: стартовая позиция карточки (этап 1 под макет).
 * tourOutro: последний шаг обучения экрана этапа — подпись кнопки «Завершить обучение» (или completeButtonLabel).
 */

/**
 * Устаревшие ключи пропуска тура (не читать для логики «показывать / не показывать»):
 * - глобальный на весь origin;
 * - v1: `_u{id}` без суффикса версии — после смены схемы все снова видят обучение, пока сами не отметят «не показывать».
 * - v2 → v3 (2026-04): принудительный сброс чекбокса «не показывать автотур» у всех после выкладки (новый ключ).
 */
export const SKIP_CASE_SIMULATOR_TOUR_KEY = 'simulex_skip_case_simulator_tour';

const SKIP_CASE_SIMULATOR_TOUR_KEY_V2 = 'simulex_skip_case_simulator_tour_v2';
const SKIP_CASE_SIMULATOR_TOUR_KEY_V3 = 'simulex_skip_case_simulator_tour_v3';

/** Удалить устаревшие флаги пропуска (глобальный, v1 и v2 на пользователя). Вызывать после входа и при /me. */
export function clearObsoleteSimulatorTourSkipKeys(userId) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(SKIP_CASE_SIMULATOR_TOUR_KEY);
    if (userId != null && !Number.isNaN(Number(userId))) {
      const u = Number(userId);
      localStorage.removeItem(`${SKIP_CASE_SIMULATOR_TOUR_KEY}_u${u}`);
      localStorage.removeItem(`${SKIP_CASE_SIMULATOR_TOUR_KEY_V2}_u${u}`);
    }
  } catch (_) {
    /* ignore */
  }
}

/** Снимок сессии симуляции в localStorage раньше был общим на origin — вызывал подмешивание сессий между аккаунтами. */
export const LEGACY_ACTIVE_SESSION_LOCAL_KEY = 'simulex_active_session_v1';

/** Ключ снимка сессии симуляции строго для пользователя `userId` (число id из БД). */
export function activeGameSessionLocalKey(userId) {
  if (userId == null || Number.isNaN(Number(userId))) {
    throw new Error('activeGameSessionLocalKey: нужен числовой userId');
  }
  return `${LEGACY_ACTIVE_SESSION_LOCAL_KEY}_u${Number(userId)}`;
}

/**
 * Пропуск автотура при следующем запуске кейса — только явный выбор пользователя (чекбокс в приветствии).
 * Ключ v3: не наследует общий ключ, v1 `_u{id}` и v2 `_u{id}` (после смены версии все снова видят автотур, пока не отметят чекбокс).
 */
export function skipCaseSimulatorTourLocalKey(userId) {
  if (userId == null || Number.isNaN(Number(userId))) {
    throw new Error('skipCaseSimulatorTourLocalKey: нужен числовой userId');
  }
  return `${SKIP_CASE_SIMULATOR_TOUR_KEY_V3}_u${Number(userId)}`;
}

/** Показывать полноэкранное приветствие только у кейса с таким числом этапов. */
export const INTRO_FULL_CASE_STAGE_COUNT = 4;

function getActualCasePayload(casePayload) {
  return casePayload?.case || casePayload || null;
}

function truncateIntroLine(s, maxLen) {
  const t = String(s || '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** Конфиг полноэкранного приветствия хранится в data/case*.json: `case.simulator_intro`. */
export function getSimulatorIntroConfig(casePayload) {
  const actualCase = getActualCasePayload(casePayload);
  const cfg = actualCase?.simulator_intro;
  return cfg && typeof cfg === 'object' ? cfg : null;
}

/** Включено ли приветствие для данного кейса. */
export function shouldShowSimulatorIntro(casePayload) {
  const actualCase = getActualCasePayload(casePayload);
  const stages = actualCase?.stages;
  const cfg = getSimulatorIntroConfig(actualCase);
  if (!cfg || cfg.enabled === false) return false;
  const requiredStageCount = Number(cfg.required_stage_count || INTRO_FULL_CASE_STAGE_COUNT);
  return Array.isArray(stages) && stages.length === requiredStageCount;
}

/**
 * Убрать из подписи первый абзац, если он дословно совпадает с заголовком этапа (см. `st.title` у карточки).
 */
function stripDuplicateComicCaption(heading, raw) {
  const h = (heading || '').trim();
  if (!h || !raw) return raw;
  const s = String(raw);
  const parts = s.split(/\n\n+/);
  if (parts.length >= 2 && parts[0].trim() === h) {
    return parts.slice(1).join('\n\n').trim();
  }
  if (parts.length === 1 && s.trim() === h) {
    return '';
  }
  return raw;
}

/**
 * Кадры комикса на 3-м шаге: заголовок и подпись из конфига этапа (`intro`), картинки — из `imageSrcs` или плейсхолдер.
 */
export function buildIntroComicPanelsFromStages(stages, introConfig) {
  if (!Array.isArray(stages) || stages.length !== INTRO_FULL_CASE_STAGE_COUNT) return [];
  const comicConfig = introConfig?.part_comic;
  const imgs = comicConfig?.image_srcs;
  const captions = comicConfig?.caption_overrides;
  return stages.map((st, i) => {
    const heading = (st.title || `Этап ${i + 1}`).trim();
    const rawCaption = (Array.isArray(captions) && captions[i]) || st.intro || '';
    return {
      stageOrder: i + 1,
      heading,
      caption: truncateIntroLine(stripDuplicateComicCaption(heading, rawCaption), 320),
      imageSrc: (Array.isArray(imgs) && imgs[i]) || null,
    };
  });
}

/** Общие шаги после welcome (один раз за запуск, на первом экране симулятора) */
export function getHudTourSteps({ showTimer }) {
  const steps = [
    {
      id: 'hud-mail',
      title: 'Почта',
      body: 'Сюда приходят задания и важные сообщения по кейсу. Откройте почту, чтобы не пропустить вводные.',
      highlightId: 'mail',
      expandNav: false,
    },
    {
      id: 'hud-docs',
      title: 'Документы кейса',
      body: 'Документы по кейсу, которые пригодятся, чтобы пройти каждый этап осознанно.',
      highlightId: 'docs',
      expandNav: false,
    },
    {
      id: 'hud-tutor-btn',
      title: 'ИИ-наставник',
      body: 'Задайте вопрос ИИ-наставнику: он помогает разобраться в фактах, рисках и логике кейса по содержанию, но не подсказывает «правильный» ответ и не говорит, куда нажимать в интерфейсе.',
      highlightId: 'tutor_nav_btn',
      expandNav: false,
    },
    {
      id: 'hud-nav',
      title: 'Панель навигации',
      body: 'Кнопка «…» открывает или скрывает верхнюю полосу: название этапа, завершение кейса и прочие действия.',
      highlightId: 'nav_toggle',
      expandNav: false,
    },
  ];
  if (showTimer) {
    steps.splice(1, 0, {
      id: 'hud-timer',
      title: 'Таймер',
      body: 'На этом этапе есть ограничение по времени. Следите за обратным отсчётом.',
      highlightId: 'timer',
      expandNav: false,
    });
  }
  return steps;
}

/** Отфильтровать шаги: skipIfNoElement — пропустить, если якоря нет в DOM. */
export function filterResolvableTourSteps(steps) {
  if (!Array.isArray(steps)) return [];
  return steps.filter((s) => {
    if (!s || !s.skipIfNoElement || !s.highlightId) return true;
    if (typeof document === 'undefined') return false;
    return !!document.querySelector(`[data-tutor-highlight="${s.highlightId}"]`);
  });
}

const STAGE_BY_ID = {
  'stage-1': [
    {
      id: 's1-docs-toggle',
      title: 'Два режима этапа',
      body: `## Выявление контекста сделки

**Ваша роль** — инхаус-юрист. **Сторона в кейсе** — ваша компания; нюансы контекста и письма руководителя см. в почте и материалах.

**Цель** — собрать у инициатора сделки информацию, нужную для проверки договора на риски и для переговоров с контрагентом на следующих этапах.

### Два режима экрана

- **«Сбор информации»** — чтение документов кейса, **чат с инициатором** сделки и **доска заметок**: сюда попадают выделения из текстов и выводы из диалога.
- **«Бриф сделки»** — заметки **переносятся в блоки** карты сделки (бриф). **Итог этапа** — осмысленно заполненный и **отправленный бриф** (кнопка внизу экрана брифа).

### Рекомендуемый порядок

Сначала изучите документы и задайте инициатору (руководителю проекта) уточняющие вопросы, затем переключитесь на бриф и разложите факты по блокам. Дальше по туру — отдельно: документы, **чат** (терпение и вложения), доска и область брифа.

*Тумблер переключения режима — подсвечен на экране.*`,
      highlightId: 'stage1_view_toggle',
      expandNav: false,
    },
    {
      id: 's1-doc-list',
      title: 'Документы',
      body: 'Читайте документы кейса. Можно выделять нужные фрагменты текста и фиксировать их в заметках — они понадобятся для брифа по сделке.',
      highlightId: 'stage1_document_list',
      expandNav: false,
      tourCardPlacement: 'stage1_viewport_left',
    },
    {
      id: 's1-initiator',
      title: 'Чат с инициатором',
      body: `## Диалог с руководителем проекта

Здесь вы **уточняете факты** по сделке: формулируйте вопросы чётко и по делу — это часть сбора контекста для брифа.

### Терпение

Под именем — **шкала терпения**. Она **падает**, если вы **повторяетесь**, задаёте **оффтоп** (не про проект) или просите то, что **уже дано в материалах кейса**. **Новые по смыслу вопросы по сделке** запас не уменьшают. Если шкала **обнулилась**, руководитель **перестаёт отвечать** — планируйте вопросы заранее.

### Документы из чата

Попросите нужный **файл прямо в сообщении**. Когда руководитель **присылает документ**, под ответом появляется **кнопка** (иконка файла и название) — откройте её, чтобы прочитать вложение. **Первый** запрос конкретного документа терпение **обычно не тратит**; **повторный** запрос **того же** файла — **тратит**.

Количество уже заданных вопросов считается внизу у поля ввода.`,
      highlightId: 'stage1_initiator_chat',
      expandNav: false,
      tourCardPlacement: 'stage1_main_column_center',
      tourCardAnchor: 'stage1_document_list',
    },
    {
      id: 's1-attributes',
      title: 'Доска заметок',
      body:
        'Всё, что вы выделили в текстах, сохраняется на доске заметок. Эти заметки можно использовать в режиме «Бриф сделки»: переносите их на блоки брифа. То, что не пригодилось для брифа, остаётся на доске.',
      highlightId: 'stage1_attributes_column',
      expandNav: false,
      tourCardPlacement: 'stage1_gap_doc_attributes',
      tourCardAnchor: 'stage1_document_list',
    },
    {
      id: 's1-brief',
      title: 'Область брифа',
      body:
        'Экран переключён на «Бриф сделки». Подсвечена левая колонка: блоки структурированной карты сделки. Перетащите заметки с доски справа на нужные блоки; после привязки текст можно редактировать. Для привязанных заметок доступна ИИ-проверка качества (число попыток на этап ограничено). Завершение этапа — кнопка внизу «Отправить бриф. Завершить этап».',
      highlightId: 'stage1_brief_area',
      expandNav: false,
      tourCardPlacement: 'stage1_right_of_brief',
    },
    {
      id: 's1-outro',
      title: 'Обучение этапа завершено',
      body: `## Вы настроены на сбор контекста

Кратко повторим смысл: **документы + чат + заметки** → затем **бриф по блокам** → отправка. Так вы закрываете этап осознанно, а не «для галочки».

### Советы

- Перед брифом **пройдитесь по документам** и чату: в бриф переносится то, что вы сами отметили как важное.
- Вопросы инициатору **конкретные**; следите за **терпением** и не дублируйте просьбы о тех же файлах.
- В режиме брифа **проверяйте каждый блок**: пустые или общие формулировки потом дорого исправлять на рисках и в переговорах.

### Напутствие

Дальше кейс идёт своим ходом — ошибаться нормально, важно **связывать факты с позицией**. Этот тур можно снова открыть в меню **«…»** на экране этапа.`,
      highlightId: null,
      expandNav: false,
      tourOutro: true,
    },
  ],
  'stage-2': [
    {
      id: 's2-goal',
      title: 'Этап 2: работа с рисками',
      body: `## Формирование позиции и рисков

На втором этапе вы **анализируете текст договора** и **работаете с рисками**. Задача — пройти договор по пунктам: отметить, где есть риск, **оценить критичность** и при необходимости **дополнить договор** недостающими условиями.

Если хотите усложнить задачу — **классифицируйте риски по типам** (в интерфейсе: юридический, операционный, финансовый, репутационный; можно выбрать несколько).

### Что вы делаете на этапе

- Назначаете **уровень риска** («светофор»: высокий / средний / низкий) для выбранного пункта.
- При необходимости указываете **типы риска** — смешанные ситуации нормальны.
- Отмечаете **дополнительные условия**, которые стоит внести в договор.
- В **расширенном сценарии** кейса возможны экран **обоснования** выбора типов и сверка с эталоном.

### Где искать опору

Как в мини-обучалке: в окне **«Документы»** — **«Бриф»** (ключевая информация по сделке), **«Гайд»** (внутренние правила по заключению договоров), **«Матрица»** / матрица рисков для сверки. Документы доступны **в любой момент** этапа. Следующий шаг тура как раз про это окно.

Дальше по туру: документы → пункт договора → уровень риска → типы → доп. условия → «Готово!» и проверка.`,
      highlightId: null,
      expandNav: false,
    },
    {
      id: 's2-docs',
      title: 'Документы этапа',
      body:
        'В **«Документах»** вы можете ознакомиться с: ключевой информацией по сделке (**«Бриф»**), внутренними правилами по заключению договоров (**«Гайд»**) и **матрицей рисков** для сверки (**«Матрица»** в списке слева — как в обучалке). Слева список файлов, справа текст или таблица рисков (как на этапе 1). При необходимости — **инструкции** по этапу. Окно открывается кнопкой **«Документы»** в нижней панели. После тура окно закроется, если вы его не открывали сами.',
      highlightId: 'stage2_docs_modal',
      expandNav: false,
    },
    {
      id: 's2-contract',
      title: 'Выбор пункта договора',
      body:
        'Внимательно прочитайте договор. **Кликните по пункту** — он будет выделен. Справа откроется панель выбора уровня риска (следующий шаг тура); **цветная полоса** слева у пункта появится **после** того, как выберете уровень.',
      highlightId: 'stage2_contract_area',
      expandNav: false,
    },
    {
      id: 's2-traffic',
      title: 'Светофор уровня риска',
      body:
        'Вместо одной «шкалы» в интерфейсе — **три кнопки** со **«спидометрами»**: красный — критичный, оранжевый — средний, зелёный — низкий. Выберите уровень для пункта. **Передумали?** Нажмите **«Снять отметку риска»** или смените уровень — до **«Готово!»** всё можно исправить. Дальше переходите к следующему пункту.',
      highlightId: 'stage2_risk_panel',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's2-risk-types',
      title: 'Тип риска',
      body:
        'Если усложняете задачу: вместе с уровнем можно выбрать **тип риска** — клик по чипу в строке **«Тип риска»** под пунктом (выбранный тип подсвечивается ярче). **Повторный клик** по типу снимает выбор. Доступны **юридический**, **операционный**, **финансовый**, **репутационный**; можно несколько сразу. Если строки нет (редкий режим кейса), шаг пропускается.',
      highlightId: 'stage2_risk_type_row',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's2-extra',
      title: 'Дополнительные условия',
      body:
        '**«Дополнительные условия»** — блок **«Хочу добавить в договор»**: кликните по условию из списка (чип), чтобы добавить его в **ваш вариант** договора; можно выбрать **несколько**; добавленные условия фиксируются в вашем варианте.',
      highlightId: 'stage2_extra_conditions',
      expandNav: false,
    },
    {
      id: 's2-done',
      title: 'Готово и проверка',
      body:
        '**«Готово!»** открывает сравнение **эталонной матрицы** (слева) и **вашей**. Пункты вашей матрицы **кликабельны** — по номеру пункта увидите его текст. Затем нажмите **«Отправить на проверку»**. Если проходили **повышенный уровень сложности**, возможно ещё одно задание: **обоснование** — один из **пунктов** договора с типами риска, которые вы выбрали; отметьте подходящие варианты из **фиксированного списка**.',
      highlightId: 'stage2_done_row',
      expandNav: false,
    },
    {
      id: 's2-outro',
      title: 'Обучение этапа завершено',
      body: `## Риски — основа позиции

**Поздравляем! Этап 2 пройден.** Вы разметили интерфейс **«светофора»**, документы и проверку. На практике именно здесь задаётся **тон переговоров**: что вы готовы принять, а где будете настаивать.

### Советы

- **Матрица и гайд** — не декорация: сверяйтесь перед тем как ставить «зелёный» там, где в кейсе заложен риск.
- **Типы риска** и **доп. условия** лучше заполнять сразу по пункту, пока контекст свежий.
- Экран **«Готово»** и сравнение с эталоном — шанс **учиться на расхождениях**, а не просто закрыть окно.

### Напутствие

Спокойно перепроверьте спорные пункты перед отправкой на проверку. Обучение экрана этапа можно **повторить** в меню **«…»**.`,
      highlightId: null,
      expandNav: false,
      tourOutro: true,
    },
  ],
  'stage-3': [
    {
      id: 's3-doc',
      title: 'Пункты договора',
      body: `## Согласование и переговоры

Этап имитирует **реальные переговоры по тексту договора**: вы выбираете пункт, ведёте диалог с контрагентом в чате и фиксируете **согласованные формулировки** (или несогласие).

### Зона договора

- **Список пунктов** — основная навигация: клик по пункту открывает его текст и статус (**согласовано**, в работе, требует внимания — в зависимости от логики кейса).
- Статусы помогают **планировать порядок** обсуждения и не застревать на уже закрытых вопросах.

### Что важно помнить

Аргументы и формулировки в чате **влияют на результат этапа** и на договор, который уйдёт дальше по сценарию. При необходимости открывайте **«Документы»**, чтобы свериться с брифом и материалами кейса.

Дальше по туру подсвечены переговорный чат и блок прогресса.`,
      highlightId: 'stage3_document_frame',
      expandNav: false,
    },
    {
      id: 's3-chat',
      title: 'Переговоры',
      body: 'В чате ведите диалог с контрагентом: формулировки, контраргументы, обратная связь.',
      highlightId: 'stage3_negotiation_chat',
      expandNav: false,
    },
    {
      id: 's3-progress',
      title: 'Прогресс',
      body: 'Сводка по согласованным и открытым пунктам.',
      highlightId: 'stage3_progress_block',
      expandNav: false,
    },
    {
      id: 's3-outro',
      title: 'Обучение этапа завершено',
      body: `## Переговоры — про ясность и последовательность

Вы ведёте диалог **по пунктам**: статусы и прогресс помогают не потерять нить и не забыть жёсткие темы.

### Советы

- Сначала **согласуйте каркас** (ключевые пункты), потом «косметику» — так меньше возвратов назад.
- В чате **формулируйте позицию и альтернативы**; контраргументы лучше **привязывать к фактам** из брифа и документов.
- Если застряли — откройте **«Документы»** и вернитесь к тексту пункта: часто спор из-за разного понимания формулировки.

### Напутствие

Переговоры в симуляторе отражают **компромисс в тексте** — относитесь к нему как к рабочему артефакту. Тур экрана этапа снова доступен в **«…»**.`,
      highlightId: null,
      expandNav: false,
      tourOutro: true,
    },
  ],
  'stage-4': [
    {
      id: 's4-intro',
      title: 'Начало этапа',
      body: `## Кризис и последствия

Сценарий переносит вас **после подписания договора**: как развивалось **исполнение**, какие были **вехи**, и где возникает **кризис** — правовой или репутационный удар, требующий решений.

### Что вы делаете как юрист

- Восстанавливаете **хронологию** и понимаете, *что именно* пошло не так и в какой момент.
- Проходите **диагностику кризиса**: степень угрозы, правовые основания, срочные меры.
- Выбираете **стратегию** (например, смириться с последствиями или попытаться **исправить договор задним числом** в рамках сценария).
- В некоторых ветках работаете с **вариантами формулировок** в договоре под **ограничение по времени**.

### Этот экран

Пока открыто **входящее письмо** (модалка по центру), под ним — пустая страница. Нажмите **«Закрыть»** в письме — только после этого появится **таймлайн исполнения** и кризис. Завершить этап досрочно — **зелёная кнопка внизу справа** (если разрешено кейсом).

Дальше по туру: таймлайн → диагностика → выбор стратегии → при необходимости редактор договора и исходы.`,
      highlightId: 'stage4_intro_area',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's4-timeline',
      title: 'Таймлайн и кризис',
      body:
        'События по времени: обычные вехи и кризис (точка с кризисом). Ниже — текст остановки с описанием ситуации; «Далее» переходит к диагностике.',
      highlightId: 'stage4_timeline_wrap',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's4-diagnostic',
      title: 'Диагностика кризиса',
      body:
        'Степень угрозы (один вариант), правовое основание (несколько чекбоксов), первоочередные меры. «Завершить диагностику» фиксирует ответы и запускает ветку исхода.',
      highlightId: 'stage4_diagnostic_form',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's4-choice',
      title: 'Письмо Дока: стратегия',
      body:
        '«Принять последствия» — идти дальше без правок в тексте договора. «Вернуться к моменту подписания» — экран правок с таймером, чтобы устранить правовое основание кризиса.',
      highlightId: 'stage4_doc_choice_modal',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's4-contract',
      title: 'Редактор договора',
      body:
        'По каждому пункту — варианты формулировок (A/B/C и т.п.) или оставить как есть. Обратный отсчёт — в **плашке таймера** справа вверху (как на этапе 2); по окончании выбор фиксируется. «Готово» — вернуться к сценарию с новым таймлайном.',
      highlightId: 'stage4_contract_edit_area',
      expandNav: false,
      skipIfNoElement: true,
    },
    {
      id: 's4-rest',
      title: 'Исход, второй кризис и отчёт',
      body:
        'Показывается разрешение первого кризиса (обновлённый таймлайн, текст исхода, кнопка «Продолжить»). Далее — второй кризис: снова таймлайн и такая же диагностика, без возврата в редактор. В конце — завершение этапа и общий отчёт по симуляции.',
      highlightId: null,
      expandNav: false,
    },
    {
      id: 's4-outro',
      title: 'Обучение этапа завершено',
      body: `## Кризис — про решения под давлением

Этап связан **временем, таймлайном и ветками**: вы не «просто читаете», а **выбираете стратегию** и иногда **правите договор** в жёстком окне.

### Советы

- На таймлайне **не пролистывайте** события: в деталях спрятаны триггеры кризиса и логика исхода.
- В **диагностике** отмечайте то, что реально вытекает из фактов сценария — это влияет на дальнейшую ветку.
- В **редакторе** смотрите на **таймер вверху справа** и не спешите случайными кликами: лучше меньше правок, но осмысленных.

### Напутствие

Кризисные этапы проверяют **хладнокровие и процедуру**. Пройти с первого раза идеально необязательно; важно понять **последовательность экранов**. Обучение можно открыть снова в **«…»**.`,
      highlightId: null,
      expandNav: false,
      tourOutro: true,
    },
  ],
};

const TYPE_TO_STAGE_ID = {
  context: 'stage-1',
  position: 'stage-2',
  negotiation: 'stage-3',
  crisis: 'stage-4',
};

/** Шаг «кредиты» только если в DOM есть якорь (админ-метаданные). */
export const CREDITS_STEP = {
  id: 'hud-credits',
  title: 'Кредиты',
  body: 'Ресурс этапа: некоторые действия расходуют кредиты. Следите за балансом в шапке.',
  highlightId: 'credits',
  expandNav: true,
  skipIfNoElement: true,
};

export function getStageTourSteps(stageId, stageType) {
  if (stageId && STAGE_BY_ID[stageId]) return STAGE_BY_ID[stageId];
  const mapped = stageType && TYPE_TO_STAGE_ID[stageType];
  if (mapped && STAGE_BY_ID[mapped]) return STAGE_BY_ID[mapped];
  return [];
}

/** Только HUD (и опционально кредиты) — после приветствия, до тура экрана этапа. */
export function buildHudOnlyTourStepList(opts) {
  const { showTimer, includeCreditsStep } = opts;
  const hud = getHudTourSteps({ showTimer });
  const creditStep = includeCreditsStep ? [CREDITS_STEP] : [];
  return [...hud, ...creditStep];
}

/**
 * Собрать список шагов: HUD + опционально credits + этап (для совместимости / отладки).
 * @param {{ showTimer: boolean, stageId?: string, stageType?: string, includeCreditsStep?: boolean }} opts
 */
export function buildTourStepList(opts) {
  const { showTimer, stageId, stageType, includeCreditsStep } = opts;
  const hudOnly = buildHudOnlyTourStepList({ showTimer, includeCreditsStep });
  const stageSteps = getStageTourSteps(stageId, stageType);
  return [...hudOnly, ...stageSteps];
}
